import _ from "lodash";
/*--external-- */
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import classNames from "classnames";

/*--internal--*/
import { Button, Tag } from "@toddle-edu/ds-web";
import KonvaCanvas from "UIComponents/KonvaCanvas";
import UIModal from "UIComponents/UIModal";
import { EyeHiddenOutlined, EyeOutlined } from "@toddle-edu/ds-icons";

/**--relative--*/
import classes from "./FindHotspot.scss";
import {
  FIND_HOTSPOT_CATEGORY,
  FIND_HOTSPOT_ITEM_TYPE,
} from "./FindHotspotConstant";
import {
  isDropzone,
  stableOrderizeData,
  getInitPropsForNewShape,
  getDragAndDropCanvasWidth,
  sanitizeHotspotQuestionData,
} from "./FindHotspotUtils";
import DragAndDropCanvasHeader from "../../DragAndDropField/DragAndDropCanvasHeader/DragAndDropCanvasHeader";
import { generateRandomId, generateRandomNumber } from "Utils";
import ElementContextMenu from "../ElementContextMenu";

/**
 *
 * @param {*} props
 * @returns Canvas Header(Add Items & Fullscreen) and Canvas
 */
const FindHotspot = props => {
  const {
    t,
    mode,
    data = [],
    config = {},
    answers = [],
    isFullscreen = false,
    findHotspotAnswers = [],
    onUpdateData = () => {},
    onToggleFullscreen = () => {},
    worksheetMode,
    isFromInternalTool,
  } = props;

  const dataRef = useRef([]);
  const [drawMode, setDrawMode] = useState({});
  const [viewSolution, setViewSolution] = useState(false);
  const fieldId = _.get(config, "fieldId", "");

  // * in draft mode, showing correct answer in MODE_ASSESSMENT_VIEW
  useEffect(() => {
    if (mode === "solution_view" || mode === "view") {
      setViewSolution(mode === "solution_view");
    }
  }, [mode]);

  /**
   * Add new item into data and redraw
   */
  const handleAddNewItem = useCallback(
    item => {
      const itemKeys = _.split(_.get(item, "key"), "#");
      const category = _.first(itemKeys);
      const type = _.get(itemKeys, "1", FIND_HOTSPOT_ITEM_TYPE.TEXT);
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const payload = { t, type, item, category, canvasId };
      const newItem = getInitPropsForNewShape(payload);

      // * if hotspot start drawing on canvas : [Dropzone === Hotspot]
      if (isDropzone(category)) {
        setDrawMode({ type: type, isDrawing: true });
        return;
      }

      setDrawMode({});
      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode, fieldId]
  );

  /**
   * Update data on change in canvas
   */
  const handleItemsChange = useCallback(
    updatedItem => {
      dataRef.current = _.map(dataRef.current, item => {
        if (_.isEqual(_.get(updatedItem, "id"), _.get(item, "id"))) {
          return {
            ...(_.isObject(item) ? item : {}),
            ...(_.isObject(updatedItem) ? updatedItem : {}),
          };
        } else {
          return item;
        }
      });
      const updatedData = stableOrderizeData(dataRef.current);
      onUpdateData(updatedData, {});
    },
    [mode]
  );

  /**
   * On draw complete, add item into data and redraw
   */
  const handleDrawComplete = useCallback(
    payload => {
      let item = {
        id: generateRandomId(),
        type: _.get(drawMode, "type"),
        category: FIND_HOTSPOT_CATEGORY.DROPZONE,
      };
      if (item.type === FIND_HOTSPOT_ITEM_TYPE.POLYGON) {
        item.points = payload;
      } else if (
        item.type === FIND_HOTSPOT_ITEM_TYPE.ELLIPSE ||
        item.type === FIND_HOTSPOT_ITEM_TYPE.RECTANGLE
      ) {
        item = { ...item, ...payload };
      }
      setDrawMode({});
      const updatedData = stableOrderizeData([...dataRef.current, item]);
      setTimeout(() => onUpdateData(updatedData));
    },
    [drawMode]
  );

  /**
   * Reset state if draw is cancelled
   */
  const handleDrawCancel = useCallback(() => {
    setDrawMode({});
  }, [mode]);

  /**
   * On duplicating element update id and add new element
   */
  const handleDuplicateItem = useCallback(
    id => {
      const item = _.find(dataRef.current, { id });
      if (!item) return;

      const newItem = _.cloneDeep(item);
      newItem.id = generateRandomId();
      if (_.has(newItem, "x")) newItem.x = generateRandomNumber(10, 100);
      if (_.has(newItem, "y")) newItem.y = generateRandomNumber(10, 100);
      if (_.has(newItem, "points")) {
        newItem.points = _.map(newItem.points, point => point + 40);
      }
      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode]
  );

  /**
   *
   */
  const handleCorrectAnswer = useCallback(
    _findHotspotAnswers => {
      const updatedData = stableOrderizeData(dataRef.current);
      onUpdateData(updatedData, null, _findHotspotAnswers);
    },
    [mode, findHotspotAnswers, _.get(config, "allowToSelectMultipleHotspot")]
  );

  /**
   * Remove item from data and redraw
   */
  const handleItemRemove = useCallback(
    id => {
      const filterdElements = _.filter(
        dataRef.current,
        item => _.get(item, "id") !== id
      );
      const filteredAnswer = _.filter(findHotspotAnswers, hotspotId => {
        return _.find(filterdElements, { id: hotspotId });
      });
      onUpdateData(filterdElements, null, filteredAnswer);
    },
    [mode, findHotspotAnswers]
  );

  /**
   * Order element on canvas - zIndex management
   */
  const handleElementOrder = useCallback(
    (elementId, orderType) => {
      const elements = stableOrderizeData(_.clone(dataRef.current || []));
      const index = _.findIndex(elements, { id: elementId });
      const category = _.get(elements, [index, "category"]);
      const elementsCount = _.get(_.countBy(elements, { category }), "true", 0);

      // * no ordering: if, no elements or elements is less than 2
      if (index < 0 || !_.isArray(elements) || elementsCount < 2) return;

      const minIndex = _.findIndex(elements, { category });
      const maxIndex = minIndex + elementsCount - 1;
      const newIndex = orderType === "DOWN" ? index - 1 : index + 1;

      if (newIndex < minIndex || newIndex > maxIndex) return;

      // * removing from current position
      const elementToMove = _.first(elements.splice(index, 1));
      if (!elementToMove) return;

      // * inserting element to new position
      elements.splice(newIndex, 0, elementToMove);
      onUpdateData(elements);
    },
    [mode]
  );

  /**
   * Save student response on hotspot click
   */
  const handleHotspotClick = useCallback(
    (_, submissionData) => {
      const updatedData = stableOrderizeData(dataRef.current);
      onUpdateData(updatedData, submissionData);
    },
    [mode, config]
  );

  /**
   * Show context menu for shapes in create mode
   */
  const elementContextMenu = useCallback(
    node => {
      return (
        <ElementContextMenu
          node={node}
          onRemove={handleItemRemove}
          onOrderElement={handleElementOrder}
          onToggleAnswer={handleCorrectAnswer}
          findHotspotAnswers={findHotspotAnswers}
        />
      );
    },
    [
      findHotspotAnswers,
      handleItemRemove,
      handleCorrectAnswer,
      handleElementOrder,
    ]
  );

  /**
   * Memoized canvas component
   */
  const canvasComponent = useMemo(() => {
    dataRef.current = data || [];

    // * canvas width for different mode
    const canvasWidth = getDragAndDropCanvasWidth(isFullscreen);

    // * updating json data to required formate for canvas
    const canvasData = sanitizeHotspotQuestionData(_.cloneDeep(data), {
      mode,
      drawMode,
    });

    return (
      <KonvaCanvas
        mode={mode}
        key={fieldId}
        fieldId={fieldId}
        data={canvasData}
        width={canvasWidth}
        drawMode={drawMode}
        worksheetMode={worksheetMode}
        contextMenu={elementContextMenu}
        type={_.get(config, "fieldKey", "")}
        onChange={handleItemsChange}
        onClick={handleHotspotClick}
        onDrawCancel={handleDrawCancel}
        onRemove={handleItemRemove}
        onDuplicate={handleDuplicateItem}
        onDrawComplete={handleDrawComplete}
        options={{
          viewSolution,
          findHotspotAnswers,
          studentAnswers: answers,
          isSubmitted: _.get(config, "isSubmitted", false),
          allowInteraction: _.get(config, "allowInteraction", false),
          allowToShowHotspot: _.get(config, "allowToShowHotspot", false),
          showViewSolutionButton: _.get(
            config,
            "showViewSolutionButton",
            false
          ),
          allowToSelectMultipleHotspot: _.get(
            config,
            "allowToSelectMultipleHotspot",
            false
          ),
          isTeacherStudentViewMode: _.get(
            config,
            "isTeacherStudentViewMode",
            false
          ),
        }}
      />
    );
  }, [
    mode,
    config,
    answers,
    drawMode,
    viewSolution,
    isFullscreen,
    findHotspotAnswers,
    JSON.stringify(data),
  ]);

  if (isFullscreen) {
    return (
      <UIModal
        isFullscreen
        isOpen={isFullscreen}
        modalContent={classes.modalContent}
        onRequestClose={onToggleFullscreen}
        testId="worksheet-findHotspot-closeModal"
      >
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          isFullscreen={isFullscreen}
          onAddNewItem={handleAddNewItem}
          onDrawCancel={handleDrawCancel}
          onDrawComplete={handleDrawComplete}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>
          <div style={{ position: "relative" }}>{canvasComponent}</div>
        </div>
      </UIModal>
    );
  }

  const showViewSolutionTag =
    viewSolution && _.get(config, "showViewSolutionButton");
  const containerClassName = classNames(classes.container, {
    [classes.containerOverflowHidden]: mode !== "create",
    [classes.containerViewSolution]: showViewSolutionTag,
  });

  return (
    <>
      <div className={containerClassName}>
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          onAddNewItem={handleAddNewItem}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>{canvasComponent}</div>
        {showViewSolutionTag && (
          <div className={classes.viewSolutionTagWrapper}>
            <Tag
              _legacy_size="x-small"
              size="small"
              color="teal"
              dsVersion="2.0"
            >
              {t("worksheet:viewing_solution")}
            </Tag>
          </div>
        )}
      </div>
      {/* View solution will be visible for teacher evaluation mode to view correct answer mode */}
      {_.get(config, "showViewSolutionButton") && (
        <div className={classes.viewSolutionBtnWrapper}>
          <Button
            size="medium"
            variant="neutral"
            dsVersion="2.0"
            type="outlined"
            _legacy_size="small"
            _legacy_variant="outlined"
            icon={viewSolution ? <EyeHiddenOutlined /> : <EyeOutlined />}
            onClick={() => setViewSolution(prev => !prev)}
          >
            {t(
              viewSolution
                ? "worksheet:hide_solution"
                : "worksheet:view_solution"
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default FindHotspot;

FindHotspot.defaultProps = {};

FindHotspot.propTypes = {};
